import { Video, AlertCircle, RotateCw } from 'lucide-react';
import type { LiveSummary } from '@multimod/shared';
import { useActiveLives } from './hooks.js';
import { LiveCard } from './LiveCard.js';

interface Props {
  onSelect: (live: LiveSummary) => void;
  onAttach?: (live: LiveSummary) => void;
}

export function LivesList({ onSelect, onAttach }: Props) {
  const { data, isLoading, error, refetch, isFetching } = useActiveLives();

  if (isLoading) {
    return <div className="text-sm text-fg-muted py-4 text-center">Carregando lives ativas…</div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <AlertCircle size={32} strokeWidth={1.5} className="text-destructive" aria-hidden="true" />
        <p className="text-sm text-destructive">Erro ao buscar lives.</p>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-border bg-bg-card hover:bg-bg-muted hover:border-border-strong transition-colors duration-fast disabled:opacity-60"
        >
          <RotateCw
            size={12}
            strokeWidth={2}
            className={isFetching ? 'animate-spin' : ''}
            aria-hidden="true"
          />
          Tentar novamente
        </button>
      </div>
    );
  }

  const lives = data ?? [];
  if (lives.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Video size={40} strokeWidth={1.5} className="text-fg-subtle" aria-hidden="true" />
        <div className="space-y-1 max-w-[260px]">
          <p className="text-sm text-fg">Nenhuma live ativa</p>
          <p className="text-xs text-fg-muted">
            Não encontramos lives Twitch nos canais que você modera. A lista atualiza a cada 30 segundos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-border bg-bg-card hover:bg-bg-muted hover:border-border-strong transition-colors duration-fast disabled:opacity-60"
        >
          <RotateCw
            size={12}
            strokeWidth={2}
            className={isFetching ? 'animate-spin' : ''}
            aria-hidden="true"
          />
          Atualizar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {lives.map((l) => (
        <LiveCard
          key={l.liveChatId}
          live={l}
          onSelect={onSelect}
          {...(onAttach ? { onAttach } : {})}
        />
      ))}
    </div>
  );
}
